import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Download, ChevronRight, ChevronLeft, Plus, Trash2,
  Target, Lightbulb, TrendingUp, Calendar, DollarSign, BarChart3,
  Shield, BookOpen, Sparkles, FileDown, Presentation, FileType,
  Loader2, Check, AlertCircle, Upload, Calculator, Wand2, Play,
  RefreshCw, Wrench, Users, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { AnalysisModeToggle } from '../components/AnalysisModeToggle';
import LEICalculatorDrawer from '../components/LEICalculatorDrawer';
import ShareWithStakeholderPanel from '../components/ShareWithStakeholderPanel';
import { TransferFactorsCard, TransferRateResultsCard } from '../components/TransferFactorsCard';
import { TooltipProvider } from '../components/ui/tooltip';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const WIZARD_STEPS = [
  { id: 'basics', title: 'Project Basics', icon: FileText, description: 'Name and describe your initiative' },
  { id: 'outcomes', title: 'Target Outcomes', icon: Target, description: 'What KPIs are you improving?' },
  { id: 'interventions', title: 'Interventions', icon: Lightbulb, description: 'How will you achieve this?' },
  { id: 'impact', title: 'Impact & ROI', icon: TrendingUp, description: 'Expected financial results' },
  { id: 'timeline', title: 'Timeline', icon: Calendar, description: 'Implementation schedule' },
  { id: 'investment', title: 'Investment', icon: DollarSign, description: 'Budget breakdown' },
  { id: 'confidence', title: 'Confidence', icon: Shield, description: 'Risk and scenario analysis' },
  { id: 'evidence', title: 'Evidence', icon: BookOpen, description: 'Supporting data and research' },
  { id: 'generate', title: 'Generate', icon: Sparkles, description: 'Create your prospectus' },
  { id: 'compare', title: 'Compare', icon: BarChart3, description: 'Compare with other scenarios' }
];

export default function ProspectusBuilderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { prospectusId } = useParams();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [prospectus, setProspectus] = useState({
    title: '',
    project_name: '',
    organization_name: user?.profile?.organization_name || '',
    currency: 'USD',
    target_kpis: [],
    interventions: [],
    lei_improvement_targets: {},
    kpi_impact_projections: {},
    timeline: {
      start_date: new Date().toISOString().split('T')[0],
      implementation_months: 3,
      ramp_up_months: 3,
      full_impact_months: 6,
      total_duration_months: 12
    },
    milestones: [],
    total_investment: 0,
    investment_breakdown: [],
    roi_projection: 0,
    payback_period_months: 0,
    net_financial_impact: 0,
    annual_financial_impact: 0,
    confidence_level: 'medium',
    monte_carlo_results: null,
    scenario_analysis: null,
    confidence_rationale: '',
    internal_evidence: [],
    external_evidence: [],
    narratives: {},
    // Transfer rate factors
    manager_support: 'informed',
    job_alignment: 'role_relevant',
    time_to_apply: '2_4_weeks'
  });
  
  const [leiMeasures, setLeiMeasures] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [error, setError] = useState(null);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // New state for enhancements
  const [enhancingNarrative, setEnhancingNarrative] = useState({});
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [calculatingImpact, setCalculatingImpact] = useState(false);
  const [runningSimulation, setRunningSimulation] = useState(false);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);
  const [lastCalculatedHash, setLastCalculatedHash] = useState(null);

  // Load reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = secureStorage.getItem('session_token');
        const headers = { Authorization: `Bearer ${token}` };
        
        const [measuresRes, kpisRes, scenariosRes] = await Promise.all([
          api.get(`/lei-measures`),
          api.get(`/kpis`),
          api.get(`/scenarios`, { headers })
        ]);
        
        setLeiMeasures(measuresRes.data);
        setKpis(kpisRes.data);
        setScenarios(scenariosRes.data);
      } catch (err) {
        logger.error('Error loading data:', err);
      }
    };
    fetchData();
  }, []);

  // Load existing prospectus if editing
  useEffect(() => {
    if (prospectusId) {
      loadProspectus(prospectusId);
    }
  }, [prospectusId]);

  // Auto-clear error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadProspectus = async (id) => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.get(`/prospectus/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectus(response.data);
    } catch (err) {
      setError('Failed to load prospectus');
    } finally {
      setLoading(false);
    }
  };

  const loadFromScenario = async (scenarioId) => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(`/prospectus/from-scenario/${scenarioId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspectus(response.data);
      setShowLoadDialog(false);
      navigate(`/prospectus/${response.data.prospectus_id}`);
    } catch (err) {
      setError('Failed to load from scenario');
    } finally {
      setLoading(false);
    }
  };

  const saveProspectus = async () => {
    setSaving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      if (prospectus.prospectus_id) {
        await api.put(`/prospectus/${prospectus.prospectus_id}`, prospectus, { headers });
      } else {
        const response = await api.post(`/prospectus`, prospectus, { headers });
        setProspectus(response.data);
        navigate(`/prospectus/${response.data.prospectus_id}`, { replace: true });
      }
    } catch (err) {
      setError('Failed to save prospectus');
    } finally {
      setSaving(false);
    }
  };

  const generateNarratives = async () => {
    if (!prospectus.prospectus_id) {
      await saveProspectus();
    }
    
    setGenerating(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/${prospectus.prospectus_id}/generate-narratives`,
        { sections: ['all'], tone: 'professional', audience: 'executive' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProspectus(prev => ({ ...prev, narratives: response.data.narratives }));
    } catch (err) {
      setError('Failed to generate narratives');
    } finally {
      setGenerating(false);
    }
  };

  const exportProspectus = async (format) => {
    if (!prospectus.prospectus_id) {
      await saveProspectus();
    }
    
    setExporting(format);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/${prospectus.prospectus_id}/export/${format}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extensions = { pdf: 'pdf', pptx: 'pptx', docx: 'docx' };
      link.setAttribute('download', `${prospectus.project_name || 'Prospectus'}_Investment_Prospectus.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  // Enhancement 1: AI Narrative Enhancement for KPI fields
  const enhanceKPINarrative = async (index, fieldType) => {
    const kpi = prospectus.target_kpis[index];
    const userInput = fieldType === 'importance' ? kpi.importance : kpi.business_impact;
    
    if (!userInput || userInput.trim().length < 5) {
      setError('Please enter your thoughts first (at least a few words)');
      return;
    }
    
    setEnhancingNarrative(prev => ({ ...prev, [`${index}-${fieldType}`]: true }));
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/enhance-kpi-narrative`,
        {
          field_type: fieldType,
          kpi_name: kpi.kpi_name || 'this KPI',
          user_input: userInput,
          target_improvement: kpi.target_improvement_percent || 10
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      updateKPI(index, fieldType, response.data.enhanced_text);
    } catch (err) {
      setError('Failed to enhance narrative');
    } finally {
      setEnhancingNarrative(prev => ({ ...prev, [`${index}-${fieldType}`]: false }));
    }
  };

  // Enhancement 3: Calculate Impact from Interventions
  // Helper to generate a hash of calculation inputs
  const getCalculationInputsHash = useCallback(() => {
    const inputs = {
      interventions: prospectus.interventions.map(i => ({
        lei_measure_id: i.lei_measure_id,
        improvement_percent: i.improvement_percent,
        learner_population: i.learner_population,
        learner_population_mode: i.learner_population_mode,
        learner_population_percent: i.learner_population_percent
      })),
      target_kpis: prospectus.target_kpis.map(k => k.kpi_id),
      total_investment: prospectus.total_investment
    };
    return JSON.stringify(inputs);
  }, [prospectus.interventions, prospectus.target_kpis, prospectus.total_investment]);

  // Track if recalculation is needed when inputs change
  useEffect(() => {
    const currentHash = getCalculationInputsHash();
    if (lastCalculatedHash && currentHash !== lastCalculatedHash) {
      setNeedsRecalculation(true);
    }
  }, [getCalculationInputsHash, lastCalculatedHash]);

  const calculateImpactFromInterventions = async () => {
    if (prospectus.interventions.length === 0 || prospectus.target_kpis.length === 0) {
      setError('Please add at least one intervention and one target KPI first');
      return;
    }
    
    if (prospectus.total_investment <= 0) {
      setError('Please enter the total investment amount on the Investment step first');
      return;
    }
    
    setCalculatingImpact(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await api.post(
        `/prospectus/calculate-impact`,
        {
          interventions: prospectus.interventions,
          target_kpis: prospectus.target_kpis,
          total_investment: prospectus.total_investment,
          headcount: user?.profile?.headcount || 2000,
          industry_type: user?.profile?.industry || 'technology',
          learning_modality: 'blended',
          dampening_factors: {
            adoption_speed: 0.8,
            leadership_alignment: 0.85,
            operational_readiness: 0.8
          },
          // Dynamic transfer rate factors (will calculate if not explicit)
          capability_transfer_rate: null,  // Let backend calculate dynamically
          manager_support: prospectus.manager_support || 'informed',
          job_alignment: prospectus.job_alignment || 'role_relevant',
          time_to_apply: prospectus.time_to_apply || '2_4_weeks',
          current_maturity: 50,
          time_horizon_months: prospectus.timeline?.total_duration_months || 12
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update prospectus with calculated values
      setProspectus(prev => ({
        ...prev,
        annual_financial_impact: response.data.annual_financial_impact,
        net_financial_impact: response.data.net_financial_impact,
        roi_projection: response.data.roi_projection,
        payback_period_months: response.data.payback_period_months,
        monte_carlo_results: response.data.monte_carlo_results,
        scenario_analysis: response.data.scenario_analysis,
        confidence_level: response.data.confidence_level,
        volume_scaling: response.data.volume_scaling,
        roi_waterfall: response.data.roi_waterfall,
        transfer_rate: response.data.transfer_rate
      }));
      
      // Update hash and clear recalculation flag
      setLastCalculatedHash(getCalculationInputsHash());
      setNeedsRecalculation(false);
    } catch (err) {
      setError('Failed to calculate impact');
    } finally {
      setCalculatingImpact(false);
    }
  };

  // Enhancement 4: Run Monte Carlo Simulation
  const runMonteCarloSimulation = async () => {
    if (prospectus.annual_financial_impact <= 0 || prospectus.total_investment <= 0) {
      setError('Please enter financial impact and investment amounts first');
      return;
    }
    
    setRunningSimulation(true);
    try {
      const token = secureStorage.getItem('session_token');
      const avgImprovement = prospectus.interventions.length > 0
        ? prospectus.interventions.reduce((sum, i) => sum + (i.improvement_percent || 0), 0) / prospectus.interventions.length
        : 10;
      
      const response = await api.post(
        `/prospectus/run-simulation`,
        {
          annual_financial_impact: prospectus.annual_financial_impact,
          total_investment: prospectus.total_investment,
          dampening_factors: {
            adoption_speed: 0.7,
            leadership_alignment: 0.8,
            operational_readiness: 0.75
          },
          current_maturity: 50,
          avg_lei_improvement: avgImprovement
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update prospectus with simulation results
      setProspectus(prev => ({
        ...prev,
        monte_carlo_results: response.data.monte_carlo_results,
        scenario_analysis: response.data.scenario_analysis,
        confidence_level: response.data.confidence_level,
        confidence_rationale: response.data.confidence_rationale
      }));
    } catch (err) {
      setError('Failed to run simulation');
    } finally {
      setRunningSimulation(false);
    }
  };

  const updateProspectus = (field, value) => {
    setProspectus(prev => ({ ...prev, [field]: value }));
  };

  const addKPI = () => {
    setProspectus(prev => ({
      ...prev,
      target_kpis: [...prev.target_kpis, {
        kpi_id: '',
        kpi_name: '',
        current_value: null,
        target_improvement_percent: 10,
        importance: '',
        business_impact: ''
      }]
    }));
  };

  const updateKPI = (index, field, value) => {
    setProspectus(prev => {
      const newKpis = [...prev.target_kpis];
      newKpis[index] = { ...newKpis[index], [field]: value };
      if (field === 'kpi_id') {
        const kpi = kpis.find(k => k.id === value);
        if (kpi) newKpis[index].kpi_name = kpi.name;
      }
      return { ...prev, target_kpis: newKpis };
    });
  };

  const removeKPI = (index) => {
    setProspectus(prev => ({
      ...prev,
      target_kpis: prev.target_kpis.filter((_, i) => i !== index)
    }));
  };

  const addIntervention = () => {
    // Default learner population to workforce headcount from profile
    const defaultHeadcount = user?.profile?.headcount || 2000;
    setProspectus(prev => ({
      ...prev,
      interventions: [...prev.interventions, {
        lei_measure_id: '',
        lei_measure_name: '',
        current_score: 50,
        target_score: 70,
        improvement_percent: 20,
        implementation_approach: '',
        key_activities: [],
        learner_population: defaultHeadcount,
        learner_population_mode: 'absolute',
        learner_population_percent: 100
      }]
    }));
  };

  // Copy learner population to all interventions
  const copyLearnerPopulationToAll = (sourceIndex) => {
    setProspectus(prev => {
      const source = prev.interventions[sourceIndex];
      const newInterventions = prev.interventions.map((intervention, idx) => ({
        ...intervention,
        learner_population: source.learner_population,
        learner_population_mode: source.learner_population_mode,
        learner_population_percent: source.learner_population_percent
      }));
      return { ...prev, interventions: newInterventions };
    });
  };

  const updateIntervention = (index, field, value) => {
    setProspectus(prev => {
      const newInterventions = [...prev.interventions];
      newInterventions[index] = { ...newInterventions[index], [field]: value };
      if (field === 'lei_measure_id') {
        const measure = leiMeasures.find(m => m.id === value);
        if (measure) newInterventions[index].lei_measure_name = measure.name;
      }
      if (field === 'current_score' || field === 'target_score') {
        const current = field === 'current_score' ? value : newInterventions[index].current_score;
        const target = field === 'target_score' ? value : newInterventions[index].target_score;
        newInterventions[index].improvement_percent = target - current;
      }
      return { ...prev, interventions: newInterventions };
    });
  };

  const removeIntervention = (index) => {
    setProspectus(prev => ({
      ...prev,
      interventions: prev.interventions.filter((_, i) => i !== index)
    }));
  };

  // Handle calculator result for advanced mode
  const handleCalculatorApply = (result) => {
    if (selectedIntervention !== null) {
      updateIntervention(selectedIntervention, 'current_score', result.currentScore);
      updateIntervention(selectedIntervention, 'target_score', result.targetScore);
    }
    setShowCalculator(false);
    setSelectedIntervention(null);
  };

  const addInvestmentItem = () => {
    setProspectus(prev => ({
      ...prev,
      investment_breakdown: [...prev.investment_breakdown, {
        category: '',
        description: '',
        amount: 0,
        timing: 'Year 1'
      }]
    }));
  };

  const updateInvestmentItem = (index, field, value) => {
    setProspectus(prev => {
      const newItems = [...prev.investment_breakdown];
      newItems[index] = { ...newItems[index], [field]: value };
      // Auto-calculate total investment from breakdown items
      const newTotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      return { ...prev, investment_breakdown: newItems, total_investment: newTotal };
    });
  };

  const removeInvestmentItem = (index) => {
    setProspectus(prev => {
      const newItems = prev.investment_breakdown.filter((_, i) => i !== index);
      // Auto-calculate total investment from remaining items
      const newTotal = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      return { ...prev, investment_breakdown: newItems, total_investment: newTotal };
    });
  };

  const addEvidence = () => {
    setProspectus(prev => ({
      ...prev,
      external_evidence: [...prev.external_evidence, {
        title: '',
        source: '',
        url: '',
        summary: '',
        relevance: ''
      }]
    }));
  };

  const updateEvidence = (index, field, value) => {
    setProspectus(prev => {
      const newEvidence = [...prev.external_evidence];
      newEvidence[index] = { ...newEvidence[index], [field]: value };
      return { ...prev, external_evidence: newEvidence };
    });
  };

  const removeEvidence = (index) => {
    setProspectus(prev => ({
      ...prev,
      external_evidence: prev.external_evidence.filter((_, i) => i !== index)
    }));
  };

  const formatCurrency = (amount) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[prospectus.currency] || '$';
    if (amount >= 1000000) return `${symbol}${(amount/1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${symbol}${(amount/1000).toFixed(0)}K`;
    return `${symbol}${amount.toLocaleString()}`;
  };

  const renderStep = () => {
    switch (WIZARD_STEPS[currentStep].id) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Project Name *</Label>
                <Input
                  data-testid="project-name-input"
                  value={prospectus.project_name}
                  onChange={(e) => updateProspectus('project_name', e.target.value)}
                  placeholder="e.g., Leadership Development Program"
                />
              </div>
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  data-testid="org-name-input"
                  value={prospectus.organization_name}
                  onChange={(e) => updateProspectus('organization_name', e.target.value)}
                  placeholder="Your company name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Prospectus Title</Label>
              <Input
                data-testid="title-input"
                value={prospectus.title}
                onChange={(e) => updateProspectus('title', e.target.value)}
                placeholder="e.g., Business Case: Leadership Excellence Initiative"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={prospectus.currency} onValueChange={(v) => updateProspectus('currency', v)}>
                <SelectTrigger data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Load from existing analysis?</p>
                    <p className="text-sm text-blue-700 mt-1">
                      You can import data from a saved scenario to pre-fill many fields.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => setShowLoadDialog(true)}
                    >
                      Load from Scenario
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'outcomes':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Target KPIs</h3>
                <p className="text-sm text-gray-500">What business metrics are you improving?</p>
              </div>
              <Button onClick={addKPI} size="sm" data-testid="add-kpi-btn">
                <Plus className="w-4 h-4 mr-1" /> Add KPI
              </Button>
            </div>
            
            {prospectus.target_kpis.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Target className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500">No target KPIs added yet</p>
                  <Button onClick={addKPI} variant="outline" className="mt-4">
                    Add your first KPI
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {prospectus.target_kpis.map((kpi, index) => (
                  <Card key={kpi.name || `kpi-${index}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <Badge variant="secondary">KPI {index + 1}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => removeKPI(index)}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Select KPI</Label>
                          <Select value={kpi.kpi_id} onValueChange={(v) => updateKPI(index, 'kpi_id', v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a KPI" />
                            </SelectTrigger>
                            <SelectContent>
                              {kpis.map(k => (
                                <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Target Improvement (%)</Label>
                          <Input
                            type="number"
                            value={kpi.target_improvement_percent}
                            onChange={(e) => updateKPI(index, 'target_improvement_percent', parseFloat(e.target.value))}
                          />
                        </div>
                        
                        {/* Enhancement 1: AI-enhanced importance field */}
                        <div className="space-y-2 md:col-span-2">
                          <div className="flex items-center justify-between">
                            <Label>Why is this important?</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => enhanceKPINarrative(index, 'importance')}
                              disabled={enhancingNarrative[`${index}-importance`]}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              {enhancingNarrative[`${index}-importance`] ? (
                                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enhancing...</>
                              ) : (
                                <><Wand2 className="w-4 h-4 mr-1" /> Enhance with AI</>
                              )}
                            </Button>
                          </div>
                          <Textarea
                            value={kpi.importance}
                            onChange={(e) => updateKPI(index, 'importance', e.target.value)}
                            placeholder="Enter your rough thoughts and click 'Enhance with AI' to generate a professional narrative..."
                            rows={3}
                          />
                          <p className="text-xs text-gray-500">
                            Tip: Enter your rough notes, then click "Enhance with AI" to generate a polished narrative
                          </p>
                        </div>
                        
                        {/* Enhancement 1: AI-enhanced business impact field */}
                        <div className="space-y-2 md:col-span-2">
                          <div className="flex items-center justify-between">
                            <Label>Business Impact</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => enhanceKPINarrative(index, 'business_impact')}
                              disabled={enhancingNarrative[`${index}-business_impact`]}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            >
                              {enhancingNarrative[`${index}-business_impact`] ? (
                                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enhancing...</>
                              ) : (
                                <><Wand2 className="w-4 h-4 mr-1" /> Enhance with AI</>
                              )}
                            </Button>
                          </div>
                          <Textarea
                            value={kpi.business_impact}
                            onChange={(e) => updateKPI(index, 'business_impact', e.target.value)}
                            placeholder="Enter your rough thoughts and click 'Enhance with AI' to generate a professional narrative..."
                            rows={3}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'interventions':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold">Learning Interventions</h3>
                <p className="text-sm text-gray-500">How will you achieve the improvements?</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Enhancement 2: Simple/Advanced Mode Toggle */}
                <AnalysisModeToggle 
                  isAdvanced={isAdvancedMode} 
                  onToggle={setIsAdvancedMode}
                  variant="light"
                />
                <Button onClick={addIntervention} size="sm" data-testid="add-intervention-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Intervention
                </Button>
              </div>
            </div>
            
            {/* Advanced mode info card */}
            {isAdvancedMode && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Wrench className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">Advanced Mode</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Click "Use Calculator" to open the LEI Calculator and compute scores from raw input variables.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {prospectus.interventions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <Lightbulb className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500">No interventions added yet</p>
                  <Button onClick={addIntervention} variant="outline" className="mt-4">
                    Add your first intervention
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {prospectus.interventions.map((intervention, index) => (
                  <Card key={intervention.name || `intervention-${index}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <Badge variant="secondary">Intervention {index + 1}</Badge>
                        <div className="flex gap-2">
                          {/* Enhancement 2: Calculator button in Advanced mode */}
                          {isAdvancedMode && intervention.lei_measure_id && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedIntervention(index);
                                setShowCalculator(true);
                              }}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Calculator className="w-4 h-4 mr-1" /> Use Calculator
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => removeIntervention(index)}>
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2 md:col-span-3">
                          <Label>LEI Measure</Label>
                          <Select 
                            value={intervention.lei_measure_id} 
                            onValueChange={(v) => updateIntervention(index, 'lei_measure_id', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a measure" />
                            </SelectTrigger>
                            <SelectContent>
                              {leiMeasures.map(m => (
                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Current Score (%)</Label>
                          <Input
                            type="number"
                            value={intervention.current_score}
                            onChange={(e) => updateIntervention(index, 'current_score', parseFloat(e.target.value))}
                            disabled={isAdvancedMode}
                            className={isAdvancedMode ? 'bg-gray-100' : ''}
                          />
                          {isAdvancedMode && (
                            <p className="text-xs text-gray-500">Use calculator to set</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Target Score (%)</Label>
                          <Input
                            type="number"
                            value={intervention.target_score}
                            onChange={(e) => updateIntervention(index, 'target_score', parseFloat(e.target.value))}
                            disabled={isAdvancedMode}
                            className={isAdvancedMode ? 'bg-gray-100' : ''}
                          />
                          {isAdvancedMode && (
                            <p className="text-xs text-gray-500">Use calculator to set</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Improvement</Label>
                          <div className="h-10 flex items-center">
                            <Badge variant={intervention.improvement_percent > 0 ? "default" : "secondary"}>
                              +{(intervention.improvement_percent || 0).toFixed(2)}%
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2 md:col-span-3">
                          <Label>Implementation Approach</Label>
                          <Textarea
                            value={intervention.implementation_approach}
                            onChange={(e) => updateIntervention(index, 'implementation_approach', e.target.value)}
                            placeholder="Describe how this intervention will be implemented..."
                            rows={2}
                          />
                        </div>
                        
                        {/* Learner Population Section */}
                        <div className="md:col-span-3 pt-3 mt-2 border-t">
                          <div className="flex justify-between items-center mb-3">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Users className="w-4 h-4" /> Affected Learner Population
                            </Label>
                            {prospectus.interventions.length > 1 && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => copyLearnerPopulationToAll(index)}
                                className="text-xs h-7"
                              >
                                <Copy className="w-3 h-3 mr-1" /> Copy to All
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs text-gray-500">Input Mode</Label>
                              <Select 
                                value={intervention.learner_population_mode || 'absolute'} 
                                onValueChange={(v) => {
                                  updateIntervention(index, 'learner_population_mode', v);
                                  // Auto-calculate when switching modes
                                  if (v === 'percentage') {
                                    const pct = ((intervention.learner_population || 0) / (user?.profile?.headcount || 2000)) * 100;
                                    updateIntervention(index, 'learner_population_percent', Math.min(pct, 100));
                                  } else {
                                    const abs = Math.round((intervention.learner_population_percent || 100) / 100 * (user?.profile?.headcount || 2000));
                                    updateIntervention(index, 'learner_population', abs);
                                  }
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="absolute">Absolute Number</SelectItem>
                                  <SelectItem value="percentage">% of Workforce</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {(intervention.learner_population_mode || 'absolute') === 'absolute' ? (
                              <div className="space-y-2">
                                <Label className="text-xs text-gray-500">Number of Learners</Label>
                                <Input
                                  type="number"
                                  value={intervention.learner_population || user?.profile?.headcount || 2000}
                                  onChange={(e) => updateIntervention(index, 'learner_population', parseInt(e.target.value) || 0)}
                                  className="h-9"
                                  min={0}
                                  max={user?.profile?.headcount || 100000}
                                />
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Label className="text-xs text-gray-500">% of Workforce ({(user?.profile?.headcount || 2000).toLocaleString()} total)</Label>
                                <Input
                                  type="number"
                                  value={intervention.learner_population_percent || 100}
                                  onChange={(e) => {
                                    const pct = Math.min(parseFloat(e.target.value) || 0, 100);
                                    updateIntervention(index, 'learner_population_percent', pct);
                                    updateIntervention(index, 'learner_population', Math.round((pct / 100) * (user?.profile?.headcount || 2000)));
                                  }}
                                  className="h-9"
                                  min={0}
                                  max={100}
                                  step={0.1}
                                />
                              </div>
                            )}
                            
                            <div className="space-y-2">
                              <Label className="text-xs text-gray-500">Learners Affected</Label>
                              <div className="h-9 flex items-center px-3 bg-gray-50 rounded-md border text-sm">
                                <Users className="w-4 h-4 mr-2 text-gray-400" />
                                <span className="font-medium">
                                  {(intervention.learner_population || user?.profile?.headcount || 2000).toLocaleString()}
                                </span>
                                <span className="text-gray-500 ml-1">
                                  ({((intervention.learner_population || user?.profile?.headcount || 2000) / (user?.profile?.headcount || 2000) * 100).toFixed(1)}% of workforce)
                                </span>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Impact scales with learner population but has diminishing returns at higher volumes.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'impact':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold">Financial Impact & ROI</h3>
                <p className="text-sm text-gray-500">Expected financial results from this initiative</p>
              </div>
              {/* Enhancement 3: Calculate from Interventions button */}
              <Button 
                onClick={calculateImpactFromInterventions}
                disabled={calculatingImpact}
                className={needsRecalculation ? "bg-orange-600 hover:bg-orange-700" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {calculatingImpact ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
                ) : needsRecalculation ? (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Recalculate (Inputs Changed)</>
                ) : (
                  <><Calculator className="w-4 h-4 mr-2" /> Calculate from Interventions</>
                )}
              </Button>
            </div>
            
            {/* Recalculation needed warning */}
            {needsRecalculation && (
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-900">Recalculation Needed</p>
                      <p className="text-sm text-orange-700 mt-1">
                        You've changed intervention data (learner population, improvement scores, or KPIs). Click "Recalculate" to update the financial projections.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Enhancement 3: Info card about auto-calculation */}
            {!needsRecalculation && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Calculator className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-emerald-900">Auto-Calculate Available</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        Click "Calculate from Interventions" to automatically compute financial impact, ROI, and payback based on your interventions, KPIs, and investment. You can still edit the values manually after.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Value Transfer Conditions */}
            <TooltipProvider>
              <TransferFactorsCard
                config={prospectus}
                onConfigChange={(updates) => {
                  setProspectus(prev => ({ ...prev, ...updates }));
                  setNeedsRecalculation(true);
                }}
                headcount={user?.profile?.headcount || 2000}
                showLearnerPopulation={false}
              />
            </TooltipProvider>
            
            {/* Volume Scaling Info */}
            {prospectus.volume_scaling && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-900">Learner Population Scaling Applied</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Training {prospectus.volume_scaling.total_learners?.toLocaleString() || 0} learners across {prospectus.volume_scaling.intervention_details?.length || 0} interventions.
                        Average volume efficiency: {((prospectus.volume_scaling.average_factor || 1) * 100).toFixed(1)}%
                      </p>
                      {prospectus.volume_scaling.intervention_details?.length > 0 && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {prospectus.volume_scaling.intervention_details.map((detail, idx) => (
                            <div key={detail.name || `detail-${idx}`} className="text-xs bg-blue-100 rounded p-2">
                              <span className="font-medium">{detail.volume_scaling?.bracket || 'Unknown'}</span>
                              <span className="text-blue-600 ml-2">
                                {detail.learner_population?.toLocaleString()} learners ({detail.volume_scaling?.efficiency?.toFixed(0)}% efficiency)
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ROI Waterfall Breakdown */}
            {prospectus.roi_waterfall && (
              <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-slate-700" />
                    <p className="font-medium text-slate-900">ROI Waterfall Breakdown</p>
                  </div>
                  
                  {/* Waterfall Visual */}
                  <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                    <div className="text-center">
                      <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg text-xs font-semibold border border-blue-200">
                        Learning
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{(prospectus.roi_waterfall.learning_improvement || 0).toFixed(1)}%</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <div className="text-center">
                      <div className="bg-purple-100 text-purple-800 px-3 py-2 rounded-lg text-xs font-semibold border border-purple-200">
                        Capability
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{(prospectus.roi_waterfall.capability_impact || 0).toFixed(1)}%</p>
                      <p className="text-xs text-purple-600">@{((prospectus.roi_waterfall.capability_transfer_rate || 0.8) * 100).toFixed(0)}% transfer</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <div className="text-center">
                      <div className="bg-amber-100 text-amber-800 px-3 py-2 rounded-lg text-xs font-semibold border border-amber-200">
                        KPI Impact
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{(prospectus.roi_waterfall.kpi_impact_percent || 0).toFixed(2)}%</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <div className="text-center">
                      <div className="bg-emerald-100 text-emerald-800 px-3 py-2 rounded-lg text-xs font-semibold border border-emerald-200">
                        Financial
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{formatCurrency(prospectus.roi_waterfall.financial_value)}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <div className="text-center">
                      <div className={`px-3 py-2 rounded-lg text-xs font-bold border ${(prospectus.roi_waterfall.net_roi || 0) >= 0 ? 'bg-teal-500 text-white border-teal-600' : 'bg-rose-500 text-white border-rose-600'}`}>
                        Net ROI
                      </div>
                      <p className={`text-xs mt-1 font-semibold ${(prospectus.roi_waterfall.net_roi || 0) >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
                        {((prospectus.roi_waterfall.net_roi || 0) / 100).toFixed(2)}x
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center italic">
                    {prospectus.roi_waterfall.explanation}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Transfer Rate Factor Breakdown */}
            {prospectus.transfer_rate && prospectus.transfer_rate.factors && Object.keys(prospectus.transfer_rate.factors).length > 0 && (
              <TooltipProvider>
                <TransferRateResultsCard 
                  transferRate={prospectus.transfer_rate} 
                  transferMethod={prospectus.transfer_method}
                  roeCompositeDetail={prospectus.transfer_method === 'roe_composite' ? prospectus.transfer_rate : null}
                />
              </TooltipProvider>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Annual Financial Impact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
                      {prospectus.currency === 'EUR' ? '€' : prospectus.currency === 'GBP' ? '£' : '$'}
                    </span>
                    <Input
                      type="text"
                      data-testid="annual-impact-input"
                      value={Math.round(prospectus.annual_financial_impact || 0).toLocaleString()}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                        updateProspectus('annual_financial_impact', value);
                      }}
                      className="text-lg pl-8"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Expected annual benefit (whole number)</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Net Financial Impact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
                      {prospectus.currency === 'EUR' ? '€' : prospectus.currency === 'GBP' ? '£' : '$'}
                    </span>
                    <Input
                      type="text"
                      data-testid="net-impact-input"
                      value={Math.round(prospectus.net_financial_impact || 0).toLocaleString()}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value.replace(/,/g, '')) || 0;
                        updateProspectus('net_financial_impact', value);
                      }}
                      className="text-lg pl-8"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Total benefit minus investment (whole number)</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">ROI Projection</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Input
                      type="text"
                      data-testid="roi-input"
                      value={((prospectus.roi_projection || 0) / 100).toFixed(2)}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        updateProspectus('roi_projection', value * 100);
                      }}
                      className="text-lg pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-medium">x</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Expected return on investment (e.g., 2.74x = 274%)</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Payback Period (months)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="text"
                    data-testid="payback-input"
                    value={(prospectus.payback_period_months || 0).toFixed(2)}
                    onChange={(e) => updateProspectus('payback_period_months', parseFloat(e.target.value) || 0)}
                    className="text-lg"
                  />
                  <p className="text-sm text-gray-500 mt-2">Time to recover investment</p>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">Implementation Timeline</h3>
              <p className="text-sm text-gray-500">When will you implement and see results?</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  data-testid="start-date-input"
                  value={prospectus.timeline?.start_date || ''}
                  onChange={(e) => updateProspectus('timeline', { ...prospectus.timeline, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Implementation Phase (months)</Label>
                <Input
                  type="number"
                  value={prospectus.timeline?.implementation_months || 0}
                  onChange={(e) => updateProspectus('timeline', { ...prospectus.timeline, implementation_months: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Ramp-up Period (months)</Label>
                <Input
                  type="number"
                  value={prospectus.timeline?.ramp_up_months || 0}
                  onChange={(e) => updateProspectus('timeline', { ...prospectus.timeline, ramp_up_months: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Time to Full Impact (months)</Label>
                <Input
                  type="number"
                  value={prospectus.timeline?.full_impact_months || 0}
                  onChange={(e) => updateProspectus('timeline', { ...prospectus.timeline, full_impact_months: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Total Duration (months)</Label>
                <Input
                  type="number"
                  value={prospectus.timeline?.total_duration_months || 0}
                  onChange={(e) => updateProspectus('timeline', { ...prospectus.timeline, total_duration_months: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
        );

      case 'investment':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Investment Breakdown</h3>
                <p className="text-sm text-gray-500">What will the investment be spent on?</p>
              </div>
              <Button onClick={addInvestmentItem} size="sm" data-testid="add-investment-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium text-blue-900">Total Investment</span>
                    <p className="text-xs text-blue-700">Auto-calculated from breakdown items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(prospectus.total_investment)}
                    </p>
                    <p className="text-xs text-blue-600">{prospectus.currency}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {prospectus.investment_breakdown.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <DollarSign className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500">No investment items added yet</p>
                  <Button onClick={addInvestmentItem} variant="outline" className="mt-4">
                    Add your first investment item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {prospectus.investment_breakdown.map((item, index) => (
                  <Card key={item.name || `item-${index}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <Badge variant="outline">Item {index + 1}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => removeInvestmentItem(index)}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Category</Label>
                          <Select value={item.category} onValueChange={(v) => updateInvestmentItem(index, 'category', v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Technology">Technology</SelectItem>
                              <SelectItem value="Training">Training</SelectItem>
                              <SelectItem value="Personnel">Personnel</SelectItem>
                              <SelectItem value="Content">Content</SelectItem>
                              <SelectItem value="External">External Services</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs">Description</Label>
                          <Input
                            value={item.description}
                            onChange={(e) => updateInvestmentItem(index, 'description', e.target.value)}
                            placeholder="What is this for?"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Amount ({prospectus.currency})</Label>
                          <Input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateInvestmentItem(index, 'amount', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );

      case 'confidence':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold">Confidence & Risk Analysis</h3>
                <p className="text-sm text-gray-500">How confident are you in these projections?</p>
              </div>
              {/* Enhancement 4: Run Monte Carlo button */}
              <Button 
                onClick={runMonteCarloSimulation}
                disabled={runningSimulation}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {runningSimulation ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Run Monte Carlo Simulation</>
                )}
              </Button>
            </div>
            
            {/* Enhancement 4: Info card about auto-population */}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <BarChart3 className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-purple-900">Auto-Populate Available</p>
                    <p className="text-sm text-purple-700 mt-1">
                      Click "Run Monte Carlo Simulation" to automatically calculate confidence levels, scenario analysis, and Monte Carlo statistics based on your financial data. You can override any values after.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Confidence Level</Label>
                <Select 
                  value={prospectus.confidence_level} 
                  onValueChange={(v) => updateProspectus('confidence_level', v)}
                >
                  <SelectTrigger data-testid="confidence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High - Strong evidence and track record</SelectItem>
                    <SelectItem value="medium">Medium - Reasonable assumptions with some uncertainty</SelectItem>
                    <SelectItem value="low">Low - Significant uncertainty, conservative estimates</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Confidence Rationale</Label>
                <Textarea
                  data-testid="confidence-rationale-input"
                  value={prospectus.confidence_rationale}
                  onChange={(e) => updateProspectus('confidence_rationale', e.target.value)}
                  placeholder="Explain why you have this confidence level..."
                  rows={3}
                />
              </div>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Scenario Analysis</CardTitle>
                <CardDescription>Best, median, and worst case projections</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-3 p-4 bg-emerald-50 rounded-lg">
                    <Label className="text-emerald-700">Best Case</Label>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">ROI (x)</span>
                        <Input
                          type="text"
                          value={((prospectus.scenario_analysis?.best_case_roi || 0) / 100).toFixed(2)}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            best_case_roi: (parseFloat(e.target.value) || 0) * 100
                          })}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Impact ({prospectus.currency})</span>
                        <Input
                          type="text"
                          value={Math.round(prospectus.scenario_analysis?.best_case_impact || 0).toLocaleString()}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            best_case_impact: parseFloat(e.target.value.replace(/,/g, '')) || 0
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 p-4 bg-yellow-50 rounded-lg">
                    <Label className="text-yellow-700">Median Case</Label>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">ROI (x)</span>
                        <Input
                          type="text"
                          value={((prospectus.scenario_analysis?.median_case_roi || 0) / 100).toFixed(2)}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            median_case_roi: (parseFloat(e.target.value) || 0) * 100
                          })}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Impact ({prospectus.currency})</span>
                        <Input
                          type="text"
                          value={Math.round(prospectus.scenario_analysis?.median_case_impact || 0).toLocaleString()}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            median_case_impact: parseFloat(e.target.value.replace(/,/g, '')) || 0
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 p-4 bg-rose-50 rounded-lg">
                    <Label className="text-rose-700">Worst Case</Label>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs text-gray-500">ROI (x)</span>
                        <Input
                          type="text"
                          value={((prospectus.scenario_analysis?.worst_case_roi || 0) / 100).toFixed(2)}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            worst_case_roi: (parseFloat(e.target.value) || 0) * 100
                          })}
                        />
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Impact ({prospectus.currency})</span>
                        <Input
                          type="text"
                          value={Math.round(prospectus.scenario_analysis?.worst_case_impact || 0).toLocaleString()}
                          onChange={(e) => updateProspectus('scenario_analysis', {
                            ...prospectus.scenario_analysis,
                            worst_case_impact: parseFloat(e.target.value.replace(/,/g, '')) || 0
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Monte Carlo Results</CardTitle>
                <CardDescription>Statistical analysis from 5,000 simulations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Mean ROI (x)</Label>
                    <Input
                      type="text"
                      value={((prospectus.monte_carlo_results?.roi_mean || 0) / 100).toFixed(2)}
                      onChange={(e) => updateProspectus('monte_carlo_results', {
                        ...prospectus.monte_carlo_results,
                        roi_mean: (parseFloat(e.target.value) || 0) * 100
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">P5 ROI (x)</Label>
                    <Input
                      type="text"
                      value={((prospectus.monte_carlo_results?.roi_p5 || 0) / 100).toFixed(2)}
                      onChange={(e) => updateProspectus('monte_carlo_results', {
                        ...prospectus.monte_carlo_results,
                        roi_p5: (parseFloat(e.target.value) || 0) * 100
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">P95 ROI (x)</Label>
                    <Input
                      type="text"
                      value={((prospectus.monte_carlo_results?.roi_p95 || 0) / 100).toFixed(2)}
                      onChange={(e) => updateProspectus('monte_carlo_results', {
                        ...prospectus.monte_carlo_results,
                        roi_p95: (parseFloat(e.target.value) || 0) * 100
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Prob +ROI (%)</Label>
                    <Input
                      type="text"
                      value={(prospectus.monte_carlo_results?.prob_positive_roi || 0).toFixed(2)}
                      onChange={(e) => updateProspectus('monte_carlo_results', {
                        ...prospectus.monte_carlo_results,
                        prob_positive_roi: parseFloat(e.target.value) || 0
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Prob ROI &gt; 1x (%)</Label>
                    <Input
                      type="text"
                      value={(prospectus.monte_carlo_results?.prob_roi_over_100 || 0).toFixed(2)}
                      onChange={(e) => updateProspectus('monte_carlo_results', {
                        ...prospectus.monte_carlo_results,
                        prob_roi_over_100: parseFloat(e.target.value) || 0
                      })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'evidence':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">Supporting Evidence</h3>
              <p className="text-sm text-gray-500">What data and research backs up your projections?</p>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Internal Evidence</CardTitle>
                <CardDescription>Evidence generated from this platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { key: 'monte_carlo', label: 'Monte Carlo Simulation', desc: '5,000 iteration analysis' },
                    { key: 'sensitivity', label: 'Sensitivity Analysis', desc: 'Factor variance testing' },
                    { key: 'benchmarks', label: 'Industry Benchmarks', desc: 'Comparison with peers' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                      <Switch
                        checked={prospectus.internal_evidence?.includes(item.key)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            updateProspectus('internal_evidence', [...(prospectus.internal_evidence || []), item.key]);
                          } else {
                            updateProspectus('internal_evidence', (prospectus.internal_evidence || []).filter(e => e !== item.key));
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-base">External Evidence</CardTitle>
                    <CardDescription>Research and references</CardDescription>
                  </div>
                  <Button onClick={addEvidence} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" /> Add Reference
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {prospectus.external_evidence?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No external evidence added yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {prospectus.external_evidence?.map((evidence, index) => (
                      <div key={evidence.source || `evidence-${index}`} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between">
                          <Badge variant="outline">Reference {index + 1}</Badge>
                          <Button variant="ghost" size="sm" onClick={() => removeEvidence(index)}>
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            placeholder="Title"
                            value={evidence.title}
                            onChange={(e) => updateEvidence(index, 'title', e.target.value)}
                          />
                          <Input
                            placeholder="Source (e.g., Harvard Business Review)"
                            value={evidence.source}
                            onChange={(e) => updateEvidence(index, 'source', e.target.value)}
                          />
                          <Input
                            placeholder="URL (optional)"
                            value={evidence.url}
                            onChange={(e) => updateEvidence(index, 'url', e.target.value)}
                            className="md:col-span-2"
                          />
                          <Textarea
                            placeholder="Summary of key findings..."
                            value={evidence.summary}
                            onChange={(e) => updateEvidence(index, 'summary', e.target.value)}
                            className="md:col-span-2"
                            rows={2}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 'generate':
        return (
          <div className="space-y-6">
            <div className="text-center py-8">
              <Sparkles className="w-16 h-16 mx-auto text-amber-500 mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Ready to Generate</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Your prospectus is ready! Generate AI-powered narratives and export to your preferred format.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Card className="text-center p-6">
                <TrendingUp className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
                <h4 className="font-semibold">ROI Projection</h4>
                <p className="text-3xl font-bold text-emerald-600 mt-2">{((prospectus.roi_projection || 0) / 100).toFixed(2)}x</p>
              </Card>
              <Card className="text-center p-6">
                <DollarSign className="w-10 h-10 mx-auto text-emerald-600 mb-3" />
                <h4 className="font-semibold">Net Financial Benefit</h4>
                <p className={`text-3xl font-bold mt-2 ${(prospectus.net_financial_impact || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(prospectus.net_financial_impact)}
                </p>
              </Card>
              <Card className="text-center p-6">
                <DollarSign className="w-10 h-10 mx-auto text-blue-600 mb-3" />
                <h4 className="font-semibold">Total Investment</h4>
                <p className="text-3xl font-bold text-blue-600 mt-2">{formatCurrency(prospectus.total_investment)}</p>
              </Card>
              <Card className="text-center p-6">
                <Calendar className="w-10 h-10 mx-auto text-amber-600 mb-3" />
                <h4 className="font-semibold">Payback Period</h4>
                <p className="text-3xl font-bold text-amber-600 mt-2">{(prospectus.payback_period_months || 0).toFixed(1)} mo</p>
              </Card>
              <Card className="text-center p-6">
                <Shield className="w-10 h-10 mx-auto text-purple-600 mb-3" />
                <h4 className="font-semibold">Confidence</h4>
                <p className="text-3xl font-bold text-purple-600 mt-2 capitalize">{prospectus.confidence_level}</p>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>AI Narrative Generation</CardTitle>
                <CardDescription>Generate professional narratives for each section</CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={generateNarratives} 
                  disabled={generating}
                  className="w-full"
                  size="lg"
                >
                  {generating ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating Narratives...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" /> Generate AI Narratives</>
                  )}
                </Button>
                
                {prospectus.narratives && Object.keys(prospectus.narratives).length > 0 && (
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Narratives generated!</span>
                    </div>
                    <p className="text-sm text-emerald-600 mt-1">
                      {Object.keys(prospectus.narratives).length} sections ready for export
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Export Options</CardTitle>
                <CardDescription>Download your prospectus in your preferred format</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-6 flex-col gap-2"
                    onClick={() => exportProspectus('pdf')}
                    disabled={exporting === 'pdf'}
                  >
                    {exporting === 'pdf' ? (
                      <Loader2 className="w-8 h-8 animate-spin" />
                    ) : (
                      <FileDown className="w-8 h-8 text-rose-500" />
                    )}
                    <span className="font-semibold">PDF Document</span>
                    <span className="text-xs text-gray-500">Professional report</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-6 flex-col gap-2"
                    onClick={() => exportProspectus('pptx')}
                    disabled={exporting === 'pptx'}
                  >
                    {exporting === 'pptx' ? (
                      <Loader2 className="w-8 h-8 animate-spin" />
                    ) : (
                      <Presentation className="w-8 h-8 text-orange-500" />
                    )}
                    <span className="font-semibold">PowerPoint</span>
                    <span className="text-xs text-gray-500">Presentation deck</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="h-auto py-6 flex-col gap-2"
                    onClick={() => exportProspectus('docx')}
                    disabled={exporting === 'docx'}
                  >
                    {exporting === 'docx' ? (
                      <Loader2 className="w-8 h-8 animate-spin" />
                    ) : (
                      <FileType className="w-8 h-8 text-blue-500" />
                    )}
                    <span className="font-semibold">Word Document</span>
                    <span className="text-xs text-gray-500">Editable document</span>
                  </Button>
                </div>

                <ShareWithStakeholderPanel
                  prospectusId={prospectus.prospectus_id}
                  prospectusSaved={!!prospectus.prospectus_id}
                />
              </CardContent>
            </Card>
          </div>
        );

      case 'compare':
        return (
          <div className="space-y-6">
            <div className="text-center py-8">
              <BarChart3 className="w-16 h-16 mx-auto text-blue-500 mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Compare Scenarios</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Compare this prospectus with other scenarios to find the best investment option.
              </p>
            </div>
            
            {!prospectus.prospectus_id ? (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-yellow-600 mb-3" />
                  <p className="font-medium text-yellow-800">Save First</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please save this prospectus before comparing it with others.
                  </p>
                  <Button onClick={saveProspectus} className="mt-4" disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Save & Continue
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Compare</CardTitle>
                    <CardDescription>
                      Jump to the comparison page to compare this prospectus with others
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      size="lg" 
                      className="w-full"
                      onClick={() => navigate(`/prospectus/compare?ids=${prospectus.prospectus_id}`)}
                    >
                      <BarChart3 className="w-5 h-5 mr-2" />
                      Go to Comparison Page
                    </Button>
                    <p className="text-sm text-gray-500 text-center mt-3">
                      You can select additional prospectuses to compare on the next page
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-900">This Prospectus Summary</p>
                        <div className="grid grid-cols-3 gap-4 mt-3">
                          <div>
                            <span className="text-xs text-blue-700">Investment</span>
                            <p className="font-semibold text-blue-900">{formatCurrency(prospectus.total_investment)}</p>
                          </div>
                          <div>
                            <span className="text-xs text-blue-700">ROI</span>
                            <p className="font-semibold text-emerald-600">{((prospectus.roi_projection || 0) / 100).toFixed(2)}x</p>
                          </div>
                          <div>
                            <span className="text-xs text-blue-700">Confidence</span>
                            <p className="font-semibold text-blue-900 capitalize">{prospectus.confidence_level || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`${prospectus.prospectus_id ? 'Edit' : 'New'} Business Case`} description={`Compose investment prospectuses for executive review.`} theme={__pageTheme} />
    )}>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
                        <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/prospectus')}>
                Cancel
              </Button>
              <Button onClick={saveProspectus} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Steps */}
          <div className="w-64 shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-4">
                <nav className="space-y-1">
                  {WIZARD_STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index === currentStep;
                    const isCompleted = index < currentStep;
                    
                    return (
                      <button
                        key={step.id}
                        onClick={() => setCurrentStep(index)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          isActive 
                            ? 'bg-blue-50 text-blue-700' 
                            : isCompleted 
                              ? 'text-emerald-700 hover:bg-emerald-50' 
                              : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isActive 
                            ? 'bg-blue-600 text-white' 
                            : isCompleted 
                              ? 'bg-emerald-600 text-white' 
                              : 'bg-gray-200'
                        }`}>
                          {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{step.title}</p>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  {React.createElement(WIZARD_STEPS[currentStep].icon, { className: "w-6 h-6 text-blue-600" })}
                  <div>
                    <CardTitle>{WIZARD_STEPS[currentStep].title}</CardTitle>
                    <CardDescription>{WIZARD_STEPS[currentStep].description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {renderStep()}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                onClick={() => setCurrentStep(Math.min(WIZARD_STEPS.length - 1, currentStep + 1))}
                disabled={currentStep === WIZARD_STEPS.length - 1}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Load from Scenario Dialog */}
      <Dialog open={showLoadDialog} onOpenChange={setShowLoadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Load from Scenario</DialogTitle>
            <DialogDescription>
              Select a saved scenario to pre-fill your prospectus
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {scenarios.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No saved scenarios found</p>
            ) : (
              <div className="space-y-2">
                {scenarios.map(scenario => (
                  <button
                    key={scenario.scenario_id}
                    onClick={() => loadFromScenario(scenario.scenario_id)}
                    className="w-full p-4 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="font-medium">{scenario.name}</p>
                    <p className="text-sm text-gray-500">{scenario.description}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoadDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LEI Calculator Drawer (Enhancement 2) */}
      {showCalculator && selectedIntervention !== null && (
        <LEICalculatorDrawer
          open={showCalculator}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowCalculator(false);
              setSelectedIntervention(null);
            }
          }}
          measure={leiMeasures.find(m => m.id === prospectus.interventions[selectedIntervention]?.lei_measure_id)}
          onApply={(improvement) => {
            if (selectedIntervention !== null && improvement !== undefined) {
              // improvement is a number (percentage improvement)
              const currentScore = prospectus.interventions[selectedIntervention]?.current_score || 50;
              const targetScore = currentScore + (typeof improvement === 'number' ? improvement : 0);
              updateIntervention(selectedIntervention, 'target_score', targetScore);
            }
            setShowCalculator(false);
            setSelectedIntervention(null);
          }}
        />
      )}

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-rose-100 border border-rose-200 text-rose-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 text-rose-600 hover:text-rose-800">×</button>
        </div>
      )}
    </div>
    </PageShell>
  );
}
